import React, { useEffect, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import api from '../../api/axios';
import html2canvas from 'html2canvas';
import {
  useChampionTheme, TrophyIcon, GoldButton, GlowText, Confetti,
  ChampionProgress, ChampionBadge, Podium, CrownIcon,
} from '../../components/ChampionElements';

export default function CertificatePage() {
  const { webinarId } = useParams();
  const { webinar } = useOutletContext();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(false);
  const user = JSON.parse(localStorage.getItem('member_user') || 'null');
  const t = useChampionTheme();

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await api.get(`/api/member/webinars/${webinarId}/certificate/status`);
        setStatus(res.data);
      } catch (err) { console.error('Gagal memuat status sertifikat:', err); }
      finally { setLoading(false); }
    };
    fetchStatus();
  }, [webinarId]);

  const handleIssue = async () => {
    setIssuing(true);
    try {
      const res = await api.post(`/api/member/webinars/${webinarId}/certificate/issue`);
      if (res.data.certificate) setStatus({ issued: true, certificate: res.data.certificate });
      alert(res.data.message);
    } catch (err) { alert('Gagal menerbitkan sertifikat.'); }
    finally { setIssuing(false); }
  };

  const downloadCertificate = () => {
    const element = document.getElementById('certificate-preview');
    if (!element) return;
    html2canvas(element).then(canvas => {
      const link = document.createElement('a');
      link.download = `sertifikat-${user?.name || 'peserta'}.png`;
      link.href = canvas.toDataURL(); link.click();
    });
  };

  if (loading) return <div className="text-center py-5"><TrophyIcon size={48} /><p className="mt-3" style={{ color: t.gold }}>Memuat sertifikat...</p></div>;
  if (!status) return <div className="alert" style={{ backgroundColor: t.errorBg, color: t.error, borderRadius: '12px' }}>Data sertifikat tidak tersedia.</div>;

  const templateImage = webinar?.certificate_config?.template_image;
  const bgImage = templateImage ? `data:image/png;base64,${templateImage}` : webinar?.image ? `data:image/png;base64,${webinar.image}` : null;

  // ── ISSUED ──
  if (status.issued && status.certificate) {
    const cert = status.certificate;
    return (
      <Confetti active={true}>
        <div style={{ animation: 'slide-in-up 0.6s ease' }}>
          <link href="https://fonts.googleapis.com/css2?family=Pinyon+Script&family=Raleway:wght@400;700&display=swap" rel="stylesheet" />
          <div className="text-center mb-4">
            <div className="animate-trophy mb-2"><TrophyIcon size={64} /></div>
            <GlowText as="h3" style={{ fontSize: '2rem', textTransform: 'uppercase', letterSpacing: '4px' }}>
              <CrownIcon size={24} /> CHAMPION <CrownIcon size={24} />
            </GlowText>
            <p style={{ color: t.textMuted, marginTop: '4px' }}>Sertifikat telah terbit! Kamu luar biasa!</p>
          </div>
          <div className="d-flex flex-wrap justify-content-center gap-2 mb-4">
            <ChampionBadge><i className="bi bi-play-circle me-1"></i>{status.episode_progress || 100}% Episode</ChampionBadge>
            {status.quiz_passed !== undefined && (
              <ChampionBadge color={status.quiz_passed ? t.success : t.textMuted}>
                <i className={`bi ${status.quiz_passed ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>{status.quiz_passed ? 'Kuis Lulus' : 'Kuis'}
              </ChampionBadge>
            )}
            <ChampionBadge color={t.success}><i className="bi bi-award me-1"></i>Sertifikat Diterbitkan</ChampionBadge>
          </div>
          <div className="text-center mb-3">
            <GoldButton onClick={downloadCertificate} style={{ fontSize: '1rem', padding: '12px 32px' }}>
              <i className="bi bi-download me-2"></i>Download Sertifikat
            </GoldButton>
          </div>
          <div className="champion-border-shimmer" style={{ borderRadius: '20px', overflow: 'hidden', border: `3px solid ${t.gold}44`, transition: 'all 0.3s ease' }}>
            <div className="overflow-auto" style={{ borderRadius: '18px' }}>
              <div id="certificate-preview" className="mx-auto" style={{
                width: '1122px', height: '793px', backgroundImage: bgImage ? `url(${bgImage})` : undefined,
                backgroundColor: bgImage ? undefined : t.cardBg, backgroundSize: 'contain', backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
                alignItems: 'center', paddingTop: '236px', fontFamily: "'Raleway', sans-serif",
              }}>
                <div style={{ marginLeft: '50px', marginBottom: '75px', fontSize: '14px', color: '#48075a', fontFamily: "'Raleway', sans-serif" }}>
                  <b>{cert.certificate_number}</b>
                </div>
                <div style={{ fontSize: (user?.name?.length || 0) > 24 ? '37px' : '57px', color: '#3d0963', marginLeft: '250px', fontFamily: "'Pinyon Script', cursive" }}>
                  <b>{user?.name}</b>
                </div>
              </div>
            </div>
          </div>
          <p className="text-center mt-3" style={{ color: t.textMuted, fontSize: '0.8rem' }}>
            Diterbitkan:{' '}<span style={{ color: t.textMuted }}>
              {new Date(cert.issued_at).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
          </p>
        </div>
      </Confetti>
    );
  }

  // ── NOT ISSUED ──
  const steps = ['Episode', status.condition !== 'all_episodes_done' ? 'Kuis' : null, 'Sertifikat'].filter(Boolean);
  const currentPodiumStep = status.all_episodes_done
    ? (status.condition === 'all_episodes_done' || status.quiz_passed ? 2 : 1)
    : 0;

  return (
    <div style={{ animation: 'slide-in-up 0.5s ease' }}>
      <div className="text-center mb-4">
        <CrownIcon size={36} />
        <GlowText as="h5" style={{ marginTop: '8px' }}>Perjalanan Menuju Champion</GlowText>
        <p style={{ color: t.textMuted, fontSize: '0.85rem', marginTop: '4px' }}>Selesaikan persyaratan untuk mendapatkan sertifikat!</p>
      </div>
      <Podium steps={steps} currentStep={currentPodiumStep} />
      <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: '16px', padding: '24px', marginTop: '12px' }}>
        <h6 style={{ color: t.gold, fontWeight: 700, marginBottom: '16px' }}><i className="bi bi-list-check me-2"></i>Persyaratan</h6>
        <div className="mb-4">
          <div className="d-flex align-items-center mb-2">
            <span style={{ color: status.all_episodes_done ? t.success : t.textMuted }}>
              <i className={`bi me-2 ${status.all_episodes_done ? 'bi-check-circle-fill' : 'bi-circle'}`}></i>
            </span>
            <span style={{ color: t.text, fontWeight: 500 }}>Menyelesaikan semua episode</span>
          </div>
          <div style={{ marginLeft: '28px', maxWidth: '350px' }}>
            <ChampionProgress value={status.episode_progress || 0} label="" height={8} />
            <small style={{ color: t.textMuted }}>{status.episode_progress || 0}% selesai{status.all_episodes_done && <span style={{ color: t.success, marginLeft: '8px' }}><i className="bi bi-check-lg me-1"></i>Tuntas!</span>}</small>
          </div>
        </div>
        {status.condition !== 'all_episodes_done' && (
          <div className="mb-4">
            <div className="d-flex align-items-center mb-2">
              <span style={{ color: status.quiz_passed ? t.success : t.textMuted }}>
                <i className={`bi me-2 ${status.quiz_passed ? 'bi-check-circle-fill' : 'bi-circle'}`}></i>
              </span>
              <span style={{ color: t.text, fontWeight: 500 }}>
                Lulus kuis{status.quiz_passing_score ? ` (KKM ${status.quiz_passing_score})` : ''}
              </span>
            </div>
            <div style={{ marginLeft: '28px' }}>
              <small style={{ color: status.quiz_passed ? t.success : t.textMuted }}>
                {status.quiz_passed
                  ? <><i className="bi bi-trophy me-1"></i>Kuis telah lulus!</>
                  : <>Belum lulus kuis{status.quiz_passing_score ? ` — minimal nilai ${status.quiz_passing_score}` : ''}</>}
              </small>
            </div>
          </div>
        )}
        <div style={{ height: '1px', background: `linear-gradient(90deg, ${t.gold}00, ${t.gold}22, ${t.gold}00)`, marginBottom: '16px' }} />
        <GoldButton onClick={handleIssue} disabled={issuing} style={{ width: '100%', fontSize: '0.95rem', padding: '12px' }}>
          {issuing ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Memproses...</> : <><i className="bi bi-stars me-2"></i>Cek & Terbitkan Sertifikat</>}
        </GoldButton>
      </div>
    </div>
  );
}
